import { computed, type Ref } from 'vue';
import type { Session } from '@supabase/supabase-js';
import { formatFullName } from '@stoxly/shared-types';
import type { User } from '@/types/user';
import { createAppStore } from '@/lib/app-store';

/**
 * Raw auth session state. user + session are the only sources of truth; everything
 * else is DERIVED. A single setState({ user, session }) flip therefore can never
 * expose a half-state (user without session). No persistence — Supabase owns
 * session storage; this store is rebuilt from onAuthStateChange every load.
 */
export interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  /** 1-based attempt of the in-flight boot sync (0 = none) — drives boot copy. */
  bootAttempt: number;
  /** Boot sync exhausted its retries (or hit a fatal error): session KEPT. */
  bootFailed: boolean;
}

const initialState: AuthState = {
  user: null,
  session: null,
  isLoading: false,
  isInitializing: true,
  error: null,
  bootAttempt: 0,
  bootFailed: false,
};

/**
 * The TanStack-Store auth session — replaces the Pinia auth store. Mutated ONLY by
 * auth-service.ts via setState; read through the useAuth() facade
 * (src/lib/useAuth.ts), the single seam consumers go through.
 */
export const authStore = createAppStore<AuthState>({ name: 'auth', initialState });

// --- Derived reactive selectors (the facade's public reactive surface) ---

/** !!user && !!session — the single reactive auth flag. */
export function useIsAuthenticated(): Ref<boolean> {
  const user = authStore.useSelector((s) => s.user);
  const session = authStore.useSelector((s) => s.session);
  return computed(() => !!user.value && !!session.value);
}

/** user?.role === 'admin'. */
export function useIsAdmin(): Ref<boolean> {
  const user = authStore.useSelector((s) => s.user);
  return computed(() => user.value?.role === 'admin');
}

/** Boot not settled: the App.vue boot gate owns the viewport. */
export function useIsBootBlocked(): Ref<boolean> {
  const isInitializing = authStore.useSelector((s) => s.isInitializing);
  const bootFailed = authStore.useSelector((s) => s.bootFailed);
  const bootAttempt = authStore.useSelector((s) => s.bootAttempt);
  const user = authStore.useSelector((s) => s.user);
  return computed(
    () => isInitializing.value || bootFailed.value || (bootAttempt.value > 0 && !user.value),
  );
}

/** "First Last" (trimmed, blank parts dropped). */
export function useFullName(): Ref<string> {
  const user = authStore.useSelector((s) => s.user);
  return computed(() => (user.value ? formatFullName(user.value) : ''));
}

// --- Imperative snapshot for the router guard (no reactivity needed) ---

export function readAuthSnapshot(): {
  isAuthenticated: boolean;
  session: Session | null;
  isBootBlocked: boolean;
} {
  const { user, session, isInitializing, bootFailed, bootAttempt } = authStore.store.state;
  return {
    isAuthenticated: !!user && !!session,
    session,
    isBootBlocked: isInitializing || bootFailed || (bootAttempt > 0 && !user),
  };
}

/**
 * Resolve once auth finishes its initial hydration, with a hard timeout so
 * navigation is never blocked forever. The re-check AFTER subscribing closes the
 * race where isInitializing flips between the caller's check and the subscription
 * registration. Ported from the old Pinia $subscribe-based waitForAuthInit, now
 * driven off the TanStack Store subscription.
 */
export function whenAuthSettled(timeoutMs: number): Promise<void> {
  if (!authStore.store.state.isInitializing) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      subscription.unsubscribe();
      clearTimeout(timer);
      resolve();
    };

    const subscription = authStore.store.subscribe(() => {
      if (!authStore.store.state.isInitializing) finish();
    });
    const timer = setTimeout(finish, timeoutMs);

    if (!authStore.store.state.isInitializing) finish();
  });
}
